/**
 * Drawing a screen frame onto the live-view canvas.
 *
 * A frame is a picture of a payer portal, so it is treated as PHI in
 * the browser too: it is decoded straight to a bitmap, painted, and the
 * bitmap is released. No object URL, no `<img src>`, no storage, no
 * cache. `clearCanvas` wipes the last frame when the view ends so it
 * does not stay on screen.
 *
 * Kept in its own module so tests can replace it (jsdom has no canvas
 * or `createImageBitmap`).
 */

function decodeBase64(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Paint one JPEG frame. Resolves false if it could not be drawn. */
export async function drawFrame(canvas: HTMLCanvasElement, jpegBase64: string): Promise<boolean> {
  const context = canvas.getContext("2d");
  if (!context || typeof createImageBitmap !== "function") return false;
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(new Blob([decodeBase64(jpegBase64)], { type: "image/jpeg" }));
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return true;
  } catch {
    return false;
  } finally {
    bitmap?.close();
  }
}

/** Remove whatever frame is on the canvas. */
export function clearCanvas(canvas: HTMLCanvasElement | null): void {
  if (!canvas) return;
  const context = canvas.getContext("2d");
  context?.clearRect(0, 0, canvas.width, canvas.height);
}
